import React, { useEffect, useRef, useState } from "react";
import { useBrand } from "../../utils/useBrand";
import { api } from "../../services/api";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Type definitions for API response structure
interface Metric {
  label: string;
  you: number;
  them: number;
}

interface Alignment {
  title: string;
  description: string;
}

interface EnergyMatchResponse {
  summary: {
    overallScore: number;
    headline: string;
    subtext: string;
  };
  metrics: Metric[];
  alignments: Alignment[];
  tunes: Alignment[];
  insights: string;
}

const BOT_SENDER = "bot";

const EnergyMatch: React.FC = () => {
  const brand = useBrand();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // You
    yourName: "",
    yourRelationship: "Friend",
    yourDob: "",
    yourTime: "",
    yourPlace: "",
    // Other Person
    otherName: "",
    otherRelationship: "Friend",
    otherDob: "",
    otherTime: "",
    otherPlace: "",
  });
  const [showResult, setShowResult] = useState(false);
  const [historyId, setHistoryId] = useState("");
  const [apiResponse, setApiResponse] = useState<EnergyMatchResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInsightsPopup, setShowInsightsPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState<"form" | "result" | "chat">(
    "form",
  );
  const [chatMessages, setChatMessages] = useState([
    { sender: BOT_SENDER, text: "สวัสดี! วันนี้ให้ฉันช่วยอะไรดี?" },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [quickReplies, setQuickReplies] = useState<string[]>([
    "ขอคำแนะนำเพิ่มเติม",
    "ดูสรุปพลังงานอีกครั้ง",
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const handleOpenChat = () => {
    setCurrentPage("chat");
  };

  const handleBack = () => {
    navigate("/");
  };

  const handleSendMessage = async (messageText?: string) => {
    const userMessage = messageText ?? chatInput;
    if (!userMessage.trim()) return;

    setChatInput("");
    setChatMessages((prev) => [...prev, { sender: "User", text: userMessage }]);
    setIsTyping(true);

    try {
      const storedUserId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("userId")
          : null;
      const storedCategoryId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("categoryId")
          : null;
      const urlParams = new URLSearchParams(
        typeof window !== "undefined" ? window.location.search : "",
      );
      const urlCategoryId =
        urlParams.get("category") || urlParams.get("CategoryId");

      const response = await api.post("/backend/energyMatch/create", {
        userId: storedUserId || null,
        CategoryId: urlCategoryId || storedCategoryId || null,
        question: userMessage,
        chatId: historyId,
      });
      const rawResponse = response.data.aiResponse;
      const cleaned = rawResponse
        .replace(/```json/gi, "")
        .replace(/```/g, "")
        .trim();

      let healjaiResponse = cleaned;
      try {
        const parsed = JSON.parse(cleaned);
        const chatPage = parsed.pages?.find(
          (p: any) => p.pageId === "P4_ChatWithHealjai",
        );
        if (chatPage) {
          if (chatPage.chatHistory && chatPage.chatHistory.length > 0) {
            setChatMessages((prev) => [...prev, ...chatPage.chatHistory]);
            healjaiResponse = "";
          }
          if (chatPage.quickReplies) {
            setQuickReplies(chatPage.quickReplies);
          }
        }
      } catch (e) {
        // keep cleaned string if JSON parse fails
      }

      if (healjaiResponse) {
        setChatMessages((prev) => [
          ...prev,
          { sender: BOT_SENDER, text: healjaiResponse },
        ]);
      }
    } catch (err: any) {
      console.error("Chat API Error:", err);
      setChatMessages((prev) => [
        ...prev,
        {
          sender: BOT_SENDER,
          text: "ขออภัย ตอนนี้ฉันไม่สามารถประมวลผลข้อความของคุณได้ กรุณาลองอีกครั้ง",
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const formatDate = (value: string): string => {
    const cleaned = value.replace(/\D/g, "");
    let formatted = "";
    if (cleaned.length >= 1) {
      formatted += cleaned.slice(0, 2);
      if (cleaned.length >= 3) {
        formatted += "/" + cleaned.slice(2, 4);
        if (cleaned.length >= 5) {
          formatted += "/" + cleaned.slice(4, 8);
        }
      }
    }
    return formatted;
  };

  const formatTime = (value: string): string => {
    const cleaned = value.replace(/\D/g, "");
    let formatted = "";
    if (cleaned.length >= 1) {
      formatted += cleaned.slice(0, 2);
      if (cleaned.length >= 3) {
        formatted += ":" + cleaned.slice(2, 4);
      }
    }
    return formatted;
  };

  const handleDateChange = (field: string, value: string) => {
    const formatted = formatDate(value);
    setFormData((prev) => ({ ...prev, [field]: formatted }));
  };

  const handleTimeChange = (field: string, value: string) => {
    const formatted = formatTime(value);
    setFormData((prev) => ({ ...prev, [field]: formatted }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "yourDob" || name === "otherDob") {
      handleDateChange(name, value);
    } else if (name === "yourTime" || name === "otherTime") {
      handleTimeChange(name, value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation: ensure names and DOBs are filled
    if (
      !formData.yourName.trim() ||
      !formData.yourDob.trim() ||
      !formData.otherName.trim() ||
      !formData.otherDob.trim()
    ) {
      setError(
        "กรุณากรอกอย่างน้อยชื่อและวันเกิดของทั้งสองฝ่าย",
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const storedUserId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("userId")
          : null;
      const storedCategoryId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("categoryId")
          : null;

      const urlParams = new URLSearchParams(
        typeof window !== "undefined" ? window.location.search : "",
      );
      const urlCategoryId =
        urlParams.get("category") || urlParams.get("CategoryId");

      const response = await api.post("/backend/energyMatch/create", {
        userId: storedUserId || null,
        CategoryId: urlCategoryId || storedCategoryId || null,
        name: formData.yourName,
        dob: formData.yourDob,
        dob_time: formData.yourTime || "6:00 AM",
        dob_place: formData.yourPlace || "Thailand",
        name_p: formData.otherName,
        dob_p: formData.otherDob,
        dob_time_p: formData.otherTime || "6:00 AM",
        dob_place_p: formData.otherPlace || "Thailand",
        relation_p: formData.otherRelationship,
      });

      // Parse the response
      const rawResponse = response.data.aiResponse;
      const cleaned = rawResponse
        .replace(/```json/gi, "")
        .replace(/```/g, "")
        .trim();
      const parsed = JSON.parse(cleaned) as EnergyMatchResponse;

      console.log("Parsed API Response:", cleaned);

      setHistoryId(response.data.historyId);
      setApiResponse(parsed);
      setShowResult(true);
      setCurrentPage("result");
    } catch (err: any) {
      console.error("API Error:", err);
      setError(
        err.response?.data?.message ||
          "คำนวณ Energy Match ไม่สำเร็จ กรุณาลองอีกครั้ง",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTryAnother = () => {
    setShowResult(false);
    setApiResponse(null);
    setError(null);
    setCurrentPage("form");
    // Reset chat messages to initial state when trying another match
    setChatMessages([
      { sender: BOT_SENDER, text: "สวัสดี! วันนี้ให้ฉันช่วยอะไรดี?" },
    ]);
    setQuickReplies(["ขอคำแนะนำเพิ่มเติม", "ดูสรุปพลังงานอีกครั้ง"]);
    setChatInput("");
    setFormData({
      yourName: "",
      yourRelationship: "Friend",
      yourDob: "",
      yourTime: "",
      yourPlace: "",
      otherName: "",
      otherRelationship: "Friend",
      otherDob: "",
      otherTime: "",
      otherPlace: "",
    });
  };

  // Component to render message with Markdown support
  const MessageContent = ({
    text,
    isUser,
  }: {
    text: string;
    isUser: boolean;
  }) => {
    if (isUser) {
      return <div className="whitespace-pre-wrap">{text}</div>;
    }

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => (
            <p className="mb-2 last:mb-0" {...props} />
          ),
          h1: ({ node, ...props }) => (
            <h1 className="text-xl font-bold mt-4 mb-2 first:mt-0" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2
              className="text-lg font-semibold mt-3 mb-2 first:mt-0"
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              className="text-base font-semibold mt-2 mb-1 first:mt-0"
              {...props}
            />
          ),
          strong: ({ node, ...props }) => (
            <strong className="font-bold" {...props} />
          ),
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 my-2 space-y-1" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />
          ),
          li: ({ node, ...props }) => <li className="my-1" {...props} />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-3">
              <table
                className="min-w-full border-collapse border border-gray-300 text-sm"
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-gray-100" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th
              className="border border-gray-300 px-3 py-2 text-left font-semibold"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-gray-300 px-3 py-2" {...props} />
          ),
          a: ({ node, ...props }) => (
            <a
              className="text-purple-600 underline hover:text-purple-800"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className="border-l-4 border-purple-300 pl-4 my-2 italic text-gray-600"
              {...props}
            />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  // Full-screen chat view (outside card design)
  if (currentPage === "chat" && showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex flex-col">
        {/* Chat Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-6 py-4 sm:py-5 shadow-lg">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/15 text-xl sm:text-2xl font-bold text-white">
                {brand.name.charAt(0)}
              </div>
              <div>
                <div className="text-base sm:text-lg font-semibold text-white">
                  {brand.name}
                </div>
                <div className="text-xs sm:text-sm text-purple-100">ออนไลน์</div>
              </div>
            </div>
            <button
              onClick={() => setCurrentPage("result")}
              className="text-white hover:text-purple-200 transition-colors focus:outline-none bg-white/10 rounded-full px-4 py-2 text-sm font-semibold ring-1 ring-white/20 hover:bg-white/20"
              aria-label="ย้อนกลับ"
            >
              กลับไปที่ผลลัพธ์
            </button>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="max-w-3xl mx-auto space-y-4">
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${
                  msg.sender === "User" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[75%] rounded-2xl sm:rounded-3xl px-4 py-2.5 sm:px-5 sm:py-3 text-sm sm:text-base shadow-md ${
                    msg.sender === "User"
                      ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                      : "bg-white text-slate-800"
                  }`}
                >
                  <MessageContent
                    text={msg.text}
                    isUser={msg.sender === "User"}
                  />
                  <div
                    className={`mt-1.5 text-[10px] sm:text-xs ${
                      msg.sender === "User"
                        ? "text-purple-200"
                        : "text-slate-400"
                    }`}
                  >
                    {new Date().toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl sm:rounded-3xl px-4 py-3 shadow-md">
                  <div className="flex gap-2">
                    <span className="h-2 w-2 rounded-full bg-purple-600 animate-bounce" />
                    <span className="h-2 w-2 rounded-full bg-purple-600 animate-bounce" />
                    <span className="h-2 w-2 rounded-full bg-purple-600 animate-bounce" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Quick Replies and Input Area */}
        <div className="border-t border-purple-100 bg-white/80 backdrop-blur-sm px-4 sm:px-6 py-3 sm:py-4 shadow-lg">
          <div className="max-w-3xl mx-auto">
            {quickReplies.length > 0 && (
              <div className="mb-3 sm:mb-4 flex flex-wrap gap-2">
                {quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSendMessage(reply)}
                    className="rounded-full border border-purple-200 bg-white px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-purple-700 transition hover:bg-purple-50 hover:border-purple-300 shadow-sm"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 sm:gap-3">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                placeholder={`Ask ${brand.name} a question...`}
                className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 sm:py-3 text-sm sm:text-base text-slate-800 placeholder:text-slate-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-200 shadow-sm"
              />
              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={!chatInput.trim()}
                className="rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-5 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-md transition hover:shadow-lg hover:from-purple-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                ส่ง
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If result is showing, render the result component with responsive design
  if (showResult) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8 px-4 sm:p-8 flex items-center justify-center">
          <div className="w-full max-w-4xl bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-8 py-4 sm:py-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white text-center">
                ✨ Your Energy Match Result ✨
              </h1>
            </div>

            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              {/* Based on both of your energy patterns */}
              <p className="text-center text-gray-500 text-sm sm:text-base">
                {apiResponse?.summary?.subtext ||
                  "จากรูปแบบพลังงานของทั้งคู่"}
              </p>

              {/* Horizontal Bar Match Score */}
              <div className="text-center">
                <div className="max-w-lg mx-auto">
                  <div className="flex justify-between items-end mb-3">
                    <span className="text-xs sm:text-sm font-medium text-gray-500">
                      Low
                    </span>
                    <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-purple-600">
                      {apiResponse?.summary?.overallScore || 78}%
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-500">
                      สูง
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4 md:h-5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-indigo-500 h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${apiResponse?.summary?.overallScore || 78}%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-gray-700 mt-4 sm:mt-5 text-base sm:text-lg font-medium">
                    {apiResponse?.summary?.headline ||
                      "พลังงานของคุณเข้ากันได้ดี มีบางจุดที่ปรับเพิ่มได้"}
                  </p>
                </div>
              </div>

              {/* Life Graph Comparison */}
              <div className="border-t border-gray-200 pt-4 sm:pt-6">
                <h3 className="font-semibold text-gray-800 text-lg sm:text-xl mb-4 sm:mb-5 text-center">
                  เปรียบเทียบ Life Graph
                </h3>
                <div className="space-y-4 sm:space-y-5">
                  {(
                    apiResponse?.metrics || [
                      { label: "การไหลของอารมณ์", you: 85, them: 82 },
                      { label: "จังหวะความคิด", you: 72, them: 76 },
                      { label: "แรงขับเคลื่อนการกระทำ", you: 88, them: 62 },
                      { label: "สนามความกลมกลืน", you: 86, them: 84 },
                      { label: "พลังงานการสื่อสาร", you: 68, them: 71 },
                    ]
                  ).map((item, idx) => (
                    <div key={idx}>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm sm:text-base mb-2 gap-1 sm:gap-0">
                        <span className="text-gray-700 font-medium">
                          {item.label}
                        </span>
                        <div className="flex gap-3 sm:gap-4 text-xs sm:text-sm">
                          <span className="text-purple-600 font-medium">
                            You {item.you}%
                          </span>
                          <span className="text-indigo-600 font-medium">
                            Them {item.them}%
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 sm:gap-2">
                        <div className="flex-1 bg-purple-100 rounded-full h-2 sm:h-3 overflow-hidden">
                          <div
                            className="bg-purple-500 h-full rounded-full"
                            style={{ width: `${item.you}%` }}
                          ></div>
                        </div>
                        <div className="flex-1 bg-indigo-100 rounded-full h-2 sm:h-3 overflow-hidden">
                          <div
                            className="bg-indigo-500 h-full rounded-full"
                            style={{ width: `${item.them}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Strong Alignment & Areas to Tune */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                <div className="bg-green-50 rounded-lg sm:rounded-xl p-4 sm:p-5">
                  <h4 className="font-semibold text-green-700 text-base sm:text-lg mb-2 sm:mb-3">
                    ความสอดคล้องที่แข็งแกร่ง
                  </h4>
                  <ul className="space-y-2 sm:space-y-3">
                    {(
                      apiResponse?.alignments || [
                        {
                          title: "การไหลของอารมณ์",
                          description:
                            "ทั้งคู่รับรู้อารมณ์ของกันและกันได้ง่าย",
                        },
                        {
                          title: "สนามความกลมกลืน",
                          description: "การอยู่ด้วยกันให้ความรู้สึกเป็นธรรมชาติ",
                        },
                      ]
                    ).map((item, idx) => (
                      <li
                        key={idx}
                        className="text-sm sm:text-base text-gray-700"
                      >
                        <span className="font-medium">{item.title}:</span>{" "}
                        {item.description}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-amber-50 rounded-lg sm:rounded-xl p-4 sm:p-5">
                  <h4 className="font-semibold text-amber-700 text-base sm:text-lg mb-2 sm:mb-3">
                    จุดที่ควรปรับ
                  </h4>
                  <ul className="space-y-2 sm:space-y-3">
                    {(
                      apiResponse?.tunes || [
                        {
                          title: "แรงขับเคลื่อนการกระทำ",
                          description: "จังหวะการทำสิ่งต่าง ๆ ต่างกัน",
                        },
                        {
                          title: "พลังงานการสื่อสาร",
                          description:
                            "บทสนทนาอาจอยู่แค่ผิวเผิน",
                        },
                      ]
                    ).map((item, idx) => (
                      <li
                        key={idx}
                        className="text-sm sm:text-base text-gray-700"
                      >
                        <span className="font-medium">{item.title}:</span>{" "}
                        {item.description}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 pt-4 sm:pt-6">
                <button
                  onClick={() => setShowInsightsPopup(true)}
                  className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm sm:text-base font-semibold text-purple-700 shadow-sm ring-1 ring-purple-200 transition hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  ดูข้อมูลเชิงลึก
                </button>
                <button
                  onClick={handleOpenChat}
                  className="inline-flex items-center justify-center rounded-full bg-purple-600 px-5 py-3 text-sm sm:text-base font-semibold text-white shadow-lg shadow-purple-200 transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  Chat with {brand.name}
                </button>
                <button
                  onClick={handleTryAnother}
                  className="inline-flex items-center justify-center rounded-full border border-purple-200 bg-white px-5 py-3 text-sm sm:text-base font-semibold text-purple-700 transition hover:border-purple-300 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-200"
                >
                  ลองจับคู่อีกครั้ง
                </button>
              </div>
            </div>
          </div>
        </div>
        {showInsightsPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
            <div className="w-full max-w-2xl rounded-3xl bg-white p-6 sm:p-8 shadow-2xl ring-1 ring-black/10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.3em] text-purple-600">
                    สรุปข้อมูลเชิงลึก
                  </p>
                  <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                    ข้อมูลเชิงลึก
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowInsightsPopup(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
                >
                  ×
                </button>
              </div>
              <div className="mt-5 text-slate-700 text-sm sm:text-base">
                <div className="rounded-3xl bg-slate-50 p-6 text-slate-700">
                  <p className="text-xs uppercase tracking-[0.24em] text-purple-600">
                    ข้อมูลเชิงลึก
                  </p>
                  <p className="mt-4 text-base leading-7">
                    {apiResponse?.insights ||
                      "โฟกัสที่กิจกรรมร่วมกันเพื่อสร้างสมดุลให้แรงขับเคลื่อนการกระทำ ลองถามคำถามที่ลึกขึ้นเพื่อพัฒนาพลังงานการสื่อสาร"}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowInsightsPopup(false)}
                  className="rounded-full bg-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-200 transition hover:bg-purple-700"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Otherwise render the form with responsive design
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8 px-4 sm:p-8 flex items-center justify-center">
      <div className="w-full max-w-6xl bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl overflow-hidden">
        {/* Header with relative positioning for back button */}
        <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 px-4 sm:px-8 py-4 sm:py-6">
          <button
            onClick={handleBack}
            className="absolute left-4 sm:left-6 top-1/2 transform -translate-y-1/2 text-white hover:text-purple-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white rounded-lg p-2 z-10"
            aria-label="ย้อนกลับ"
          >
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white text-center">
            ✨ Energy Match ✨
          </h1>
          <p className="text-purple-100 text-center text-xs sm:text-sm md:text-base mt-1">
            กรอกข้อมูลของทั้งสองฝ่าย
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-8">
          {error && (
            <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm sm:text-base">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
            {/* You Section */}
            <div className="space-y-4 sm:space-y-5">
              <h2 className="text-xl sm:text-2xl font-semibold text-purple-800 border-b-2 border-purple-200 pb-2">
                You
              </h2>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  ชื่อ
                </label>
                <input
                  type="text"
                  name="yourName"
                  value={formData.yourName}
                  onChange={handleChange}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
                  placeholder="กรอกชื่อของคุณ"
                />
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  ความสัมพันธ์
                </label>
                <select
                  name="yourRelationship"
                  value={formData.yourRelationship}
                  onChange={handleChange}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
                >
                  <option value="Friend">เพื่อน</option>
                  <option value="Partner">คู่รัก</option>
                  <option value="Family">ครอบครัว</option>
                  <option value="Colleague">เพื่อนร่วมงาน</option>
                </select>
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  วันเกิด
                </label>
                <input
                  type="text"
                  name="yourDob"
                  value={formData.yourDob}
                  onChange={handleChange}
                  placeholder="วว / ดด / ปปปป"
                  maxLength={10}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  เวลาเกิด (ไม่บังคับ)
                </label>
                <input
                  type="text"
                  name="yourTime"
                  value={formData.yourTime}
                  onChange={handleChange}
                  placeholder="ชช : นน"
                  maxLength={5}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  สถานที่เกิด (ไม่บังคับ)
                </label>
                <input
                  type="text"
                  name="yourPlace"
                  value={formData.yourPlace}
                  onChange={handleChange}
                  placeholder="กรอกเมืองหรืออำเภอ"
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Other Person Section */}
            <div className="space-y-4 sm:space-y-5">
              <h2 className="text-xl sm:text-2xl font-semibold text-indigo-800 border-b-2 border-indigo-200 pb-2">
                อีกฝ่าย
              </h2>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  ชื่อ
                </label>
                <input
                  type="text"
                  name="otherName"
                  value={formData.otherName}
                  onChange={handleChange}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  placeholder="กรอกชื่อของเขา"
                />
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  ความสัมพันธ์
                </label>
                <select
                  name="otherRelationship"
                  value={formData.otherRelationship}
                  onChange={handleChange}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                >
                  <option value="Friend">เพื่อน</option>
                  <option value="Partner">คู่รัก</option>
                  <option value="Family">ครอบครัว</option>
                  <option value="Colleague">เพื่อนร่วมงาน</option>
                </select>
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  วันเกิด
                </label>
                <input
                  type="text"
                  name="otherDob"
                  value={formData.otherDob}
                  onChange={handleChange}
                  placeholder="วว / ดด / ปปปป"
                  maxLength={10}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  เวลาเกิด (ไม่บังคับ)
                </label>
                <input
                  type="text"
                  name="otherTime"
                  value={formData.otherTime}
                  onChange={handleChange}
                  placeholder="ชช : นน"
                  maxLength={5}
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1">
                  สถานที่เกิด (ไม่บังคับ)
                </label>
                <input
                  type="text"
                  name="otherPlace"
                  value={formData.otherPlace}
                  onChange={handleChange}
                  placeholder="กรอกเมืองหรืออำเภอ"
                  className="mt-1 w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 sm:mt-10 flex justify-center">
            <button
              type="submit"
              disabled={loading}
              className="px-6 sm:px-8 md:px-10 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-200 text-base sm:text-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {loading ? "กำลังคำนวณ..." : "🔮 เปิดเผยคู่ของคุณ 🔮"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnergyMatch;
